import { z } from 'zod'
import { uuid } from './common.js'

/**
 * The intake form: the first thing a prospective client fills in, sent by Ana
 * after the first WhatsApp conversation.
 *
 * **Budget is never asked as "what is your budget?".** Most clients cannot
 * answer that — they have no reference for what a trip costs — and a number
 * picked out of the air anchors the whole conversation. So the form asks three
 * things people CAN answer, and Ana names the budget herself after research:
 * a comfort style (cards with examples), what the last comparable trip cost
 * (a memory, not a guess), and a per-person range with an honest "I don't know
 * yet" that is a first-class answer rather than a skipped field.
 *
 * **Deliberately absent: anything identifying.** No passport, no CPF, no date
 * of birth. Those belong in `travel_documents`, collected once a trip is real
 * and held under consent. A prospect who has not hired anyone should hand over
 * the least that lets Ana reply.
 */

/** How the trip should feel. Four cards, each rendered with an example in the UI. */
export const comfortStyle = z.enum(['essential', 'comfortable', 'premium', 'luxury'])
export type ComfortStyle = z.infer<typeof comfortStyle>
export const comfortStyles = comfortStyle.options

/**
 * Spend per person, excluding flights, in BRL.
 *
 * Bands rather than a field: a typed number invites the anchor this form exists
 * to avoid. `unsure` is a real value, stored as such, so Ana can tell "did not
 * know" from "did not answer".
 */
export const budgetBand = z.enum(['upto15', 'from15to30', 'from30to60', 'above60', 'unsure'])
export type BudgetBand = z.infer<typeof budgetBand>
export const budgetBands = budgetBand.options

export const tripInterest = z.enum([
  'gastronomy',
  'culture',
  'nature',
  'beach',
  'shopping',
  'adventure',
  'wellness',
  'family',
  'celebration',
])
export type TripInterest = z.infer<typeof tripInterest>
export const tripInterests = tripInterest.options

/** Where the client says they found Ana. Free text would never be counted. */
export const intakeSource = z.enum(['instagram', 'ads', 'referral', 'whatsapp', 'other'])
export type IntakeSource = z.infer<typeof intakeSource>
export const intakeSources = intakeSource.options

export const MAX_INTAKE_TEXT = 2_000

export const intakeFormRequest = z.object({
  name: z.string().trim().min(2).max(120),
  /** Digits, with an optional leading +. Normalised server-side; Ana replies on WhatsApp. */
  whatsapp: z
    .string()
    .trim()
    .max(40)
    .transform((v) => v.replace(/[^\d+]/g, ''))
    .pipe(z.string().regex(/^\+?\d{8,15}$/)),
  email: z.string().trim().max(200).email().nullish(),

  destination: z.string().trim().min(2).max(200),
  /** As they would say it — "julho de 2027", "férias escolares" — not a date picker. */
  whenText: z.string().trim().min(1).max(200),
  durationDays: z.number().int().min(1).max(120).nullish(),
  adults: z.number().int().min(1).max(30),
  children: z.number().int().min(0).max(30).default(0),
  childrenAges: z.string().trim().max(120).nullish(),

  comfort: comfortStyle,
  budgetBand: budgetBand,
  lastTrip: z.string().trim().max(MAX_INTAKE_TEXT).nullish(),
  interests: z.array(tripInterest).max(tripInterests.length).default([]),
  notes: z.string().trim().max(MAX_INTAKE_TEXT).nullish(),
  source: intakeSource.nullish(),

  /** LGPD: the client agrees to Ana using this to reply. Must be literally true. */
  consent: z.literal(true),
  locale: z.enum(['pt-BR', 'en']).default('pt-BR'),
  /** Honeypot. A person never sees this field; a form-filling bot does. */
  website: z.string().max(200).optional(),
})
export type IntakeFormRequest = z.infer<typeof intakeFormRequest>

/** What the public page needs to render before anyone has typed a word. */
export const intakePublic = z.object({
  agencyName: z.string(),
  /** https only; the page embeds it after submit. Null until Ana has an account. */
  bookingUrl: z.string().url().nullable(),
})
export type IntakePublic = z.infer<typeof intakePublic>

export const intakeSubmission = z.object({
  id: uuid,
  createdAt: z.string(),
  handledAt: z.string().nullable(),
  name: z.string(),
  whatsapp: z.string(),
  email: z.string().nullable(),
  destination: z.string(),
  whenText: z.string(),
  durationDays: z.number().nullable(),
  adults: z.number(),
  children: z.number(),
  childrenAges: z.string().nullable(),
  comfort: comfortStyle,
  budgetBand: budgetBand,
  lastTrip: z.string().nullable(),
  interests: z.array(tripInterest),
  notes: z.string().nullable(),
  source: intakeSource.nullable(),
})
export type IntakeSubmission = z.infer<typeof intakeSubmission>

export const markIntakeHandledRequest = z.object({ handled: z.boolean() })
export type MarkIntakeHandledRequest = z.infer<typeof markIntakeHandledRequest>
